// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    CartLineModel,
    InvoiceLineModel,
    OrderLineModel,
    ProductModel,
    WishListLineModel,
} from "@insite/client-framework/Types/ApiModels";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductLineAddToListLink from "@store/Library/Components/ProductLine/ProductLineAddToListLink";
import ProductLineMessageInfosBox, {
    ProductLineMessageInfosBoxProps,
} from "@store/Library/Components/ProductLine/ProductLineMessageInfosBox";
import ProductLineProductImage from "@store/Library/Components/ProductLine/ProductLineProductImage";
import ProductLineProductTitle from "@store/Library/Components/ProductLine/ProductLineProductTitle";
import ProductLinePartNumbers, {
    ProductLinePartNumbersProps,
} from "@store/Library/Components/ProductLine/ProductLinePartNumbers";
import ProductLineRemoveLink, {
    ProductLineRemoveLinkProps,
} from "@store/Library/Components/ProductLine/ProductLineRemoveLink";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { Merge } from "@toolkit/Framework/Types/Common";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";
import ProductLineFulfillment from "@store/Library/Components/ProductLine/ProductLineFulfillment";
import ProductLineMinimumOrderQuantity from "@store/Library/Components/ProductLine/ProductLineMinimumOrderQuantity";
import ProductLineInformations, {
    ProductLineInformationsProps,
} from "@store/Library/Components/ProductLine/ProductLineInformations";
import breakpointValues, { BreakpointValueProp } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import ProductLineRequestedDates, {
    ProductLineRequestedDatesProps,
} from "@store/Library/Components/ProductLine/ProductLineRequestedDates";
import Hidden from "@insite/mobius/Hidden";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
import { productLineV2Props } from "@store/Themes/common/props/productLineV2";
import ProductLineAccessoryUpsell from "@store/Library/Components/ProductLine/ProductLineAccessoryUpsell";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
import { useIsAccessoryUpsellEnabled } from "@store/Framework/Store/Context/ContextHooks";
import ProductLineOrderInformation, {
    ProductLineOrderInformationProps,
} from "@store/Library/Components/ProductLine/ProductLineOrderInformation";
import ProductLineNotes from "@store/Library/Components/ProductLine/ProductLineNotes";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import ProductLineStatusOverlay, {
    getIsDisabledProductLineStatus,
    productLineStatusAboveOverlayZIndex,
    ProductLineStatusType,
} from "@store/Library/Components/ProductLine/ProductLineStatusOverlay";
import DisablerContext from "@insite/mobius/utilities/DisablerContext";

export type ProductLineComponentModel = Omit<
    Merge<ProductModel, Merge<Merge<CartLineModel, OrderLineModel>, Merge<InvoiceLineModel, WishListLineModel>>>,
    "pricing"
> & { pricing?: CartLineModel["pricing"] };

export interface ProductLinePresentationProps extends ExpandStyles<ProductLineStyles> {
    gap?: BreakpointValueProp;
}

export interface ProductLineProps
    extends ProductLinePresentationProps,
        Pick<ProductLinePartNumbersProps, "showLineNumber" | "showProductNumber">,
        Pick<
            ProductLineInformationsProps,
            | "deliveredAmountProps"
            | "priceProps"
            | "quantityOrderedProps"
            | "quantityRemainingProps"
            | "quantityShippedProps"
            | "showDeliveredAmount"
            | "showPrice"
            | "showQuantity"
            | "showQuantityRemaining"
            | "showQuantityShipped"
            | "showSubtotal"
        >,
        Pick<ProductLineRequestedDatesProps, "onChangeRequestedDate">,
        Pick<ProductLineOrderInformationProps, "shipmentsDisplay" | "showInvoiceStatus" | "openInvoicesInNewTab"> {
    additionalContent?: React.ReactNode;
    additionalAlternativeContent?: React.ReactNode;
    allowParentChildDisplay?: boolean;
    actionLink?: React.ReactNode;
    errorMessages?: ProductLineMessageInfosBoxProps["messages"];
    onClickRemove?: ProductLineRemoveLinkProps["onClick"];
    productLine: ProductLineComponentModel | undefined;
    productInfo?: ProductInfo;
    showAccessoryUpsell?: boolean;
    showAddToList?: boolean;
    showImage?: boolean;
    showRemove?: boolean;
    showRequestedDates?: boolean;
    showTitle?: boolean;
    showFulfillment?: boolean;
    showOrderInformation?: boolean;
    status?: ProductLineStatusType;
    warningMessages?: ProductLineMessageInfosBoxProps["messages"];
}

export interface ProductLineStyles {
    actionsWrapper?: InjectableCss;
    additionalAlternativeContentWrapper?: InjectableCss;
    additionalContentWrapper?: InjectableCss;
    mobileActionsWrapper?: InjectableCss;
    notesWrapper?: InjectableCss;
    orderInformationWrapper?: InjectableCss;
    productContentActionColumn?: InjectableCss;
    productContentColumn?: InjectableCss;
    productContentInfoColumn?: InjectableCss;
    productContentRow?: InjectableCss;
    productImageColumn?: InjectableCss;
    productInnerWrapper?: InjectableCss;
    productRow?: InjectableCss;
    productWrapper?: InjectableCss;
    removeLinkWrapper?: InjectableCss;
    requestedDatesWrapper?: InjectableCss;
    wrapper?: InjectableCss;
}

const sharedBoxStyles = (showPadding = true) => {
    return css`
        ${showPadding ? breakpointValues(productLineV2Props.boxPaddingValues, "padding") : null}
        border-top: 1px solid ${getColor("common.borderLight")};
        border-bottom-right-radius: inherit;
        border-bottom-left-radius: inherit;
    `;
};

const getBoxWrapperProps = (showPadding?: boolean): InjectableCss => {
    return {
        css: sharedBoxStyles(showPadding),
    };
};

const getBoxAltWrapperProps = (showPadding?: boolean): InjectableCss => {
    return {
        css: css`
            ${sharedBoxStyles(showPadding)}
            position: relative;
            z-index: ${productLineStatusAboveOverlayZIndex};
            background-color: ${getColor("common.accent")};
        `,
    };
};

export const productLineStyles: ProductLineStyles = {
    wrapper: productLineV2Props.wrapper,
    productWrapper: {
        css: css`
            ${breakpointValues(productLineV2Props.boxPaddingValues, "padding")}
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(6, 12)};
        `,
    },
    mobileActionsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            gap: ${pxToRem(6, 12)};
            margin-bottom: ${pxToRem(12)};

            > *:first-child:last-child {
                margin-left: auto;
            }
        `,
    },
    productRow: {
        css: css`
            ${spacingStyles(null, pxToRem(12))}
            display: flex;
            gap: ${pxToRem(16)};
        `,
    },
    productImageColumn: {
        css: css`
            flex-shrink: 0;
            ${breakpointValues(pxToRemArray([90, 132]), "width")}
        `,
    },
    productContentColumn: {
        css: css`
            flex-grow: 1;
        `,
    },
    productContentRow: {
        css: css`
            display: flex;
            gap: ${pxToRem(16)};
        `,
    },
    productContentInfoColumn: {
        css: css`
            flex-grow: 1;
        `,
    },
    productContentActionColumn: {
        css: css`
            flex-shrink: 0;
        `,
    },
    requestedDatesWrapper: getBoxAltWrapperProps(false),
    notesWrapper: getBoxWrapperProps(),
    orderInformationWrapper: getBoxWrapperProps(),
    additionalAlternativeContentWrapper: getBoxAltWrapperProps(),
    additionalContentWrapper: getBoxWrapperProps(),
};

const ProductLine: FC<ProductLineProps> = ({
    additionalAlternativeContent,
    additionalContent,
    allowParentChildDisplay,
    actionLink,
    deliveredAmountProps,
    errorMessages,
    extendedStyles,
    gap,
    onChangeRequestedDate,
    onClickRemove,
    openInvoicesInNewTab,
    overridedStyles,
    priceProps,
    productInfo,
    productLine,
    quantityOrderedProps,
    quantityRemainingProps,
    quantityShippedProps,
    shipmentsDisplay,
    showAccessoryUpsell,
    showAddToList: baseShowAddToList,
    showDeliveredAmount,
    showFulfillment,
    showImage = true,
    showInvoiceStatus,
    showLineNumber,
    showPrice,
    showProductNumber = true,
    showOrderInformation,
    showQuantity = true,
    showQuantityRemaining,
    showQuantityShipped,
    showRemove: baseShowRemove,
    showRequestedDates,
    showSubtotal,
    showTitle = true,
    status,
    warningMessages,
}) => {
    const isAccessoryUpsellEnabled = useIsAccessoryUpsellEnabled();
    const baseStyles = useExpandProps(productLineStyles, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        return mergeProps<ProductLineStyles>(baseStyles, {
            wrapper: {
                css: css`
                    ${productLineV2Props.getWrapperGapStyles(gap)}
                `,
            },
        });
    }, [baseStyles, gap]);

    if (!productLine) {
        return null;
    }

    const isParent = getBooleanProperty(productLine.properties.isParent);
    const isChild = Boolean(productLine.properties.parentRefId);

    const productTitle = showTitle && <ProductLineProductTitle productInfo={productInfo} productLine={productLine} />;

    const productImage = showImage && <ProductLineProductImage productInfo={productInfo} productLine={productLine} />;

    const showAddToList = !!baseShowAddToList && !isChild;
    const addToListLink = showAddToList && <ProductLineAddToListLink productLine={productLine} />;

    const showRemove = !!baseShowRemove && !!onClickRemove;
    const removeLink = showRemove && <ProductLineRemoveLink onClick={onClickRemove!} />;

    const partNumbers = (
        <ProductLinePartNumbers
            productLine={productLine}
            showLineNumber={showLineNumber}
            showProductNumber={showProductNumber}
        />
    );

    // Child (accessory) lines ship with their parent, so only their minimum order quantity is
    // relevant - the ships from / full truck load / lead time details are the parent's.
    const fulfillment =
        showFulfillment &&
        (isChild ? (
            <ProductLineMinimumOrderQuantity productLine={productLine} />
        ) : (
            <ProductLineFulfillment productLine={productLine} />
        ));

    const informations = (
        <ProductLineInformations
            deliveredAmountProps={deliveredAmountProps}
            priceProps={priceProps}
            productLine={productLine}
            quantityOrderedProps={quantityOrderedProps}
            quantityRemainingProps={quantityRemainingProps}
            quantityShippedProps={quantityShippedProps}
            showDeliveredAmount={showDeliveredAmount}
            showPrice={showPrice}
            showQuantity={showQuantity}
            showQuantityRemaining={showQuantityRemaining}
            showQuantityShipped={showQuantityShipped}
            showSubtotal={showSubtotal}
        />
    );

    return (
        <>
            <StyledWrapper
                {...styles.wrapper}
                className={`line${allowParentChildDisplay && isChild ? " child" : ""}`}
                data-test-selector={`cartLine_${productLine.erpNumber || productLine.productErpNumber}`}
            >
                <ProductLineStatusOverlay type={status} />
                <DisablerContext.Provider value={{ disable: getIsDisabledProductLineStatus(status) }}>
                    <StyledWrapper {...styles.productWrapper}>
                        <ProductLineMessageInfosBox messages={errorMessages} type="error" />
                        <ProductLineMessageInfosBox messages={warningMessages} type="warning" />
                        <Hidden {...styles.productInnerWrapper} above="xs" doNotRenderIfHidden>
                            {(addToListLink || removeLink || actionLink) && (
                                <StyledWrapper {...styles.mobileActionsWrapper}>
                                    {addToListLink}
                                    {removeLink}
                                    {actionLink}
                                </StyledWrapper>
                            )}
                            <StyledWrapper {...styles.productRow}>
                                <StyledWrapper {...styles.productImageColumn}>{productImage}</StyledWrapper>
                                <StyledWrapper {...styles.productContentColumn}>
                                    {productTitle}
                                    {partNumbers}
                                </StyledWrapper>
                            </StyledWrapper>
                            {fulfillment}
                            {informations}
                        </Hidden>
                        <Hidden {...styles.productInnerWrapper} below="sm" doNotRenderIfHidden>
                            <StyledWrapper {...styles.productRow}>
                                <StyledWrapper {...styles.productImageColumn}>
                                    {productImage}
                                    {addToListLink && (
                                        <StyledWrapper {...styles.actionsWrapper}>{addToListLink}</StyledWrapper>
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.productContentColumn}>
                                    <StyledWrapper {...styles.productContentRow}>
                                        <StyledWrapper {...styles.productContentInfoColumn}>
                                            {productTitle}
                                            {partNumbers}
                                            {fulfillment}
                                        </StyledWrapper>
                                        {(removeLink || actionLink) && (
                                            <StyledWrapper {...styles.productContentActionColumn}>
                                                <StyledWrapper {...styles.actionsWrapper}>
                                                    {removeLink}
                                                    {actionLink}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                    {informations}
                                </StyledWrapper>
                            </StyledWrapper>
                        </Hidden>
                    </StyledWrapper>
                    {productLine?.notes && productLine.notes.length > 0 && (
                        <StyledWrapper {...styles.notesWrapper}>
                            <ProductLineNotes productLine={productLine} />
                        </StyledWrapper>
                    )}
                    {showOrderInformation && shipmentsDisplay && shipmentsDisplay.length > 0 && (
                        <StyledWrapper
                            {...styles.orderInformationWrapper}
                            data-test-selector="product_line_order_information"
                        >
                            <ProductLineOrderInformation
                                shipmentsDisplay={shipmentsDisplay}
                                showInvoiceStatus={showInvoiceStatus}
                                openInvoicesInNewTab={openInvoicesInNewTab}
                            />
                        </StyledWrapper>
                    )}
                    {additionalContent && (
                        <StyledWrapper {...styles.additionalContentWrapper}>{additionalContent}</StyledWrapper>
                    )}
                </DisablerContext.Provider>
                {showRequestedDates && (
                    <StyledWrapper {...styles.requestedDatesWrapper}>
                        <ProductLineRequestedDates onChangeRequestedDate={onChangeRequestedDate} />
                    </StyledWrapper>
                )}
                {additionalAlternativeContent && (
                    <StyledWrapper {...styles.additionalAlternativeContentWrapper}>
                        {additionalAlternativeContent}
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {showAccessoryUpsell && isAccessoryUpsellEnabled && !isParent && !isChild && (
                <ProductLineAccessoryUpsell
                    className={`line${allowParentChildDisplay ? " child" : ""}`}
                    gap={gap}
                    productInfo={productInfo}
                    productLine={productLine}
                />
            )}
        </>
    );
};

export default ProductLine;
